import axios from "axios";
import { getTenantSubdomain } from "./subdomain";
import { listarPendientes, eliminarPendiente, actualizarIntentoPendiente } from "./offlineDb";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000/api";

// Cliente "directo" para reintentar la cola de pendientes: a propósito NO es
// el mismo `api` de src/api/client.ts, porque ese, si la petición vuelve a
// fallar por falta de conexión, la volvería a encolar (duplicándola). Aquí,
// si falla, simplemente se deja donde estaba en la cola para el siguiente
// intento.
const apiDirecta = axios.create({ baseURL: API_URL });
apiDirecta.interceptors.request.use((config) => {
  const token = localStorage.getItem("vetflow_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["x-tenant-subdomain"] = getTenantSubdomain();
  return config;
});

let sincronizando = false;

// Manda al servidor, en el orden en que se capturaron, todas las acciones
// que quedaron pendientes mientras no había conexión. Se detiene en el
// primer error real de conexión (para no perder el orden ni saturar de
// reintentos), pero sigue adelante si un pendiente individual fue
// rechazado por el servidor (ej. datos inválidos) — se descarta y se avisa,
// en vez de bloquear a los demás pendientes para siempre.
export async function sincronizarPendientes(): Promise<{ enviados: number; fallidos: number }> {
  if (sincronizando || !navigator.onLine) return { enviados: 0, fallidos: 0 };
  sincronizando = true;
  let enviados = 0;
  let fallidos = 0;
  try {
    const pendientes = await listarPendientes();
    for (const item of pendientes.sort((a, b) => a.creado - b.creado)) {
      try {
        await (apiDirecta as any)[item.metodo](item.url, item.metodo === "delete" ? undefined : item.datos);
        await eliminarPendiente(item.id!);
        enviados++;
      } catch (err: any) {
        if (!err.response) {
          // Se volvió a caer la conexión a medio camino — se detiene aquí,
          // los que faltan se reintentan en la siguiente pasada.
          break;
        }
        // El servidor sí contestó, pero rechazó la acción (ej. validación).
        // Se guarda el motivo y se descarta tras varios intentos fallidos
        // para no dejar la cola atorada indefinidamente con un dato malo.
        await actualizarIntentoPendiente(item.id!, err.response?.data?.error || err.message);
        fallidos++;
        if ((item.intentos ?? 0) >= 4) {
          await eliminarPendiente(item.id!);
        }
      }
    }
  } finally {
    sincronizando = false;
    window.dispatchEvent(new Event("mascotasvet:pendientes-cambiaron"));
  }
  return { enviados, fallidos };
}
